/*
 * API client。
 *
 * 唯一职责：把网络结果翻译成上层能渲染的、有区分度的失败原因。
 *
 * 旧项目的 axios 封装把所有失败折叠成一句 "梦境灵力不稳，请稍后再试试吧~"，
 * 硬编码在 packages/components/src/chat/chat.ts:224。结果是「后端没起」
 * 「登录过期」「限流」「供应商挂了」在界面上长得一模一样，用户和排障的人
 * 都拿不到任何信息。这里把失败分成 7 类，界面据此给不同的出路。
 *
 * 不变量：
 *   - 不在这里塞任何角色 Prompt、Skill 选择、灵力规则或记忆判断。
 *   - Provider 密钥永不进入客户端。
 *   - Access Token 不读写 localStorage，凭证走 httpOnly Cookie（credentials: same-origin）。
 */

/** 失败分类。上层按这个决定文案、是否给重试、是否跳登录。 */
export type ApiFailureKind =
  /** 请求根本没到服务端：后端没起、断网、DNS/证书失败、被拦截。 */
  | "offline"
  /** 到了但超时。可能已经产生副作用，重试必须带同一个幂等键。 */
  | "timeout"
  /** 凭证无效或过期。 */
  | "unauthorized"
  /** 已登录但无权限，或被风控阻断。 */
  | "forbidden"
  /** 服务端返回了错误状态码。 */
  | "http"
  /** 状态码正常但响应体不是约定的信封格式。 */
  | "malformed";

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status: number | undefined;
  readonly requestId: string;
  readonly retryable: boolean;
  /**
   * 服务端的稳定业务码，如 `BAD_CODE` / `CODE_COOLDOWN` / `NO_SESSION`。
   * HTTP 状态码只有几十个，分不出「验证码错了」和「登录过期了」。
   */
  readonly code: string;
  /** 错误体里的其余字段，如限流返回的 `retryAfterSeconds`。 */
  readonly details: Readonly<Record<string, unknown>>;

  constructor(init: {
    kind: ApiFailureKind;
    message: string;
    requestId: string;
    status?: number;
    retryable: boolean;
    code?: string;
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.kind = init.kind;
    this.status = init.status;
    this.requestId = init.requestId;
    this.retryable = init.retryable;
    this.code = init.code ?? init.kind.toUpperCase();
    this.details = Object.freeze({ ...init.details });
  }

  /** 限流类错误的建议等待秒数。没有就返回 null，不猜。 */
  get retryAfterSeconds(): number | null {
    const value = this.details["retryAfterSeconds"];
    return typeof value === "number" && value > 0 ? Math.ceil(value) : null;
  }
}

const DEFAULT_TIMEOUT_MS = 8000;

const newRequestId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** 后端统一信封。字段名与 apps/api 的 EnvelopeInterceptor 对齐。 */
interface Envelope<T> {
  requestId?: string;
  data?: T;
  /** 错误体除 code / message 外还可能带业务字段，如 retryAfterSeconds。 */
  error?: { code?: string; message?: string } & Record<string, unknown>;
}

interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * 请求体。
   *
   * 传 `FormData` 时走 multipart：**不 stringify，也不设 Content-Type**。
   * 那个头必须让浏览器自己填，因为它要在里面带上 boundary；
   * 手动写成 `multipart/form-data` 而没有 boundary，服务端解析出来是空的。
   */
  body?: unknown;
  method?: "GET" | "POST" | "PATCH" | "DELETE";
}

/**
 * CSRF Token。
 *
 * 凭据走 httpOnly Cookie，浏览器会在跨站请求里自动带上它们，所以写操作
 * 必须再带一个「只有本站脚本读得到」的证明。服务端登录时下发一个**非**
 * httpOnly 的 `miao_csrf` Cookie，这里读出来放进请求头。
 *
 * 跨站页面能让浏览器发 Cookie，但读不到别人域下的 Cookie 值，也就填不出这个头。
 *
 * 每次请求现读而不是缓存：登录、续期、登出都会改写它，缓存住就会在
 * 登录后的第一个写请求上失败一次。
 */
const CSRF_COOKIE = "miao_csrf";

/**
 * 导出是给绕过 `apiRequest` 的传输用的（SSE 只能自己拼 fetch）。
 * 走 `apiRequest` 的代码不需要碰它——那边自动带。
 */
export function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() !== CSRF_COOKIE) continue;
    const raw = part.slice(eq + 1).trim();
    return raw ? decodeURIComponent(raw) : null;
  }
  return null;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const requestId = newRequestId();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort("timeout"), timeoutMs);

  // 外部取消（组件卸载、用户点「停止」）与超时共用一个 signal。
  const onExternalAbort = () => controller.abort("external");
  options.signal?.addEventListener("abort", onExternalAbort, { once: true });

  const method = options.method ?? (options.body === undefined ? "GET" : "POST");
  const csrf = SAFE_METHODS.has(method) ? null : readCsrfToken();
  // multipart 的 Content-Type 必须由浏览器生成（它要带 boundary），不能手写。
  const isMultipart = typeof FormData !== "undefined" && options.body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      signal: controller.signal,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Request-Id": requestId,
        ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        ...(options.body === undefined || isMultipart
          ? {}
          : { "Content-Type": "application/json" }),
      },
      ...(options.body === undefined
        ? {}
        : { body: isMultipart ? (options.body as FormData) : JSON.stringify(options.body) }),
    });
  } catch (cause) {
    clearTimeout(timer);
    // 外部主动取消不算失败，原样抛给调用方处理。
    if (options.signal?.aborted) throw cause;

    const timedOut = controller.signal.reason === "timeout";
    throw new ApiError({
      kind: timedOut ? "timeout" : "offline",
      message: timedOut ? `请求超过 ${timeoutMs}ms 未响应` : "无法连接到服务端",
      requestId,
      retryable: true,
    });
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", onExternalAbort);
  }

  // 先解析响应体再分类。
  //
  // 反过来做（看到 401 就直接抛「登录已失效」）会毁掉登录页：用户填错验证码
  // 时后端返回的是 401 + BAD_CODE + "验证码错误或已过期"，界面却显示
  // 「登录状态已失效」，然后把他踢回登录页——他本来就在登录页。
  let envelope: Envelope<T> | null = null;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    envelope = null;
  }

  if (!response.ok) {
    const { code, message, ...details } = envelope?.error ?? {};
    const kind: ApiFailureKind =
      response.status === 401
        ? "unauthorized"
        : response.status === 403
          ? "forbidden"
          : "http";

    throw new ApiError({
      kind,
      message: message ?? defaultMessageFor(response.status),
      requestId: envelope?.requestId ?? requestId,
      status: response.status,
      ...(code ? { code } : {}),
      details,
      // 5xx 与 429 可以原样重试，其余 4xx 重试没有意义。
      retryable: response.status >= 500 || response.status === 429,
    });
  }

  if (!envelope || envelope.data === undefined) {
    throw new ApiError({
      kind: "malformed",
      message: "响应体不符合约定格式",
      requestId: envelope?.requestId ?? requestId,
      status: response.status,
      retryable: false,
    });
  }

  return envelope.data;
}

function defaultMessageFor(status: number): string {
  if (status === 401) return "登录状态已失效";
  if (status === 403) return "当前账号没有权限执行该操作";
  return `服务端返回 ${status}`;
}

/** 供离线态文案使用的人类可读描述。不含角色台词——那属于角色，不属于传输层。 */
export function describeFailure(error: unknown): {
  kind: ApiFailureKind | "unknown";
  title: string;
  detail: string;
  retryable: boolean;
} {
  if (!(error instanceof ApiError)) {
    return {
      kind: "unknown",
      title: "出了点意外",
      detail: error instanceof Error ? error.message : String(error),
      retryable: true,
    };
  }

  const table: Record<ApiFailureKind, { title: string; detail: string }> = {
    offline: {
      title: "连不上服务端",
      detail: "请求没有送达。可能是网络断开，也可能是服务端没有运行。",
    },
    timeout: {
      title: "服务端没有响应",
      detail: "请求已发出但超时。重试会复用同一个幂等键，不会重复扣费。",
    },
    unauthorized: {
      title: "登录状态已失效",
      // 服务端给了具体原因就用它：登录页上「验证码错误」和「登录过期」
      // 都是 401，套同一句话会让用户莫名其妙。
      detail: error.message || "需要重新登录后才能继续。",
    },
    forbidden: {
      title: "没有权限",
      detail: error.message || "服务端拒绝了这次操作。",
    },
    http: {
      title: "服务端返回了错误",
      detail: `${error.message}（HTTP ${error.status ?? "?"}）`,
    },
    malformed: {
      title: "响应格式不对",
      detail: "服务端返回了非约定格式，前端不做猜测式解析。",
    },
  };

  const entry = table[error.kind];
  return { kind: error.kind, ...entry, retryable: error.retryable };
}
